import { Commit, Renew } from '@carbon/icons-react';
import { GitHubProvisioningStatus } from '@gql/gql-types';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';

import { GitHubProvisioning } from '../gihub-coding-page';
import { RefetchFn } from '../utils';
import { GithubCodingCard } from './github-coding-components';
import { GithubWorkflowRun } from './github-workflow-run';

export const GithubCommits = ({
  provisioning,
  refetch,
}: {
  provisioning?: GitHubProvisioning | null;
  refetch: RefetchFn;
}) => {
  const [lastSync, setLastSync] = useState<Date>(new Date());

  const handleRefresh = useCallback(() => {
    refetch({ withGitHubSync: true });
    setLastSync(new Date());
  }, [refetch]);

  if (
    !provisioning ||
    provisioning.status !== GitHubProvisioningStatus.UserPushedCode ||
    !provisioning.commits ||
    provisioning.commits.length === 0
  ) {
    return null;
  }

  const latestCommits = provisioning.commits.slice(0, 3);

  return (
    <GithubCodingCard>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='subtitle1'>Latest commits</Typography>
        <IconButton size='small' onClick={handleRefresh}>
          <Renew size={20} />
        </IconButton>
      </Box>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Last sync: {lastSync?.toLocaleString() || 'Never'}
      </Typography>
      <Stack spacing={2}>
        {latestCommits.map((commit) => (
          <Box key={commit.sha} sx={{ borderLeft: '3px solid #e0e0e0', pl: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Stack direction='column'>
                <Typography variant='body2' fontWeight={500}>
                  {commit.message}
                </Typography>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                  <Commit size={16} />
                  <Typography variant='caption'>
                    {commit.sha.substring(0, 7)}
                  </Typography>
                </Stack>
                <Typography variant='body2' color='text.secondary'>
                  by {commit.author}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {new Date(commit.date).toLocaleString()}
                </Typography>
              </Stack>
              <GithubWorkflowRun workflowRun={commit.latestWorkflowRun} />
            </Box>
          </Box>
        ))}
      </Stack>
    </GithubCodingCard>
  );
};
